// Chemin: C:\gestion-copro\src\services\checklist.service.ts
// Service Chantier 1 — SENTINELLE relance (checklist documentaire)
// Session 110
// Session 161 : Ajout attestation_immatriculation_rnc — alignement 16 docs avec contextBuilder SENTINELLE
// Session 244 : Matching prioritaire sur checklistItemId (posé par SENTINELLE à l'analyse)
//               Fallback mots-clés conservé pour documents sans checklistItemId
//               Migration rétroactive : migrerChecklistItemIds() pour documents existants
//
// Responsabilités :
// 1. Générer la checklist personnalisée selon le profil onboarding (neuve vs ancienne)
// 2. Matcher les documents déposés (collection Firestore) avec les items attendus
//    — Passe 1 : matching direct sur checklistItemId (fiable, posé par SENTINELLE)
//    — Passe 2 fallback : matching par mots-clés (pour documents sans checklistItemId)
// 3. Calculer l'état global (complétude, alertes critiques)
// 4. Gérer les relances progressives (J+3, J+10, J+21, mensuelle)
// 5. Vérifier la complétude des interlocuteurs (syndic, promoteur)
// 6. Migrer rétroactivement les documents existants (migrerChecklistItemIds)

import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from './firebase';
import {
  ChecklistItem,
  ChecklistItemId,
  ChecklistState,
  RelanceState,
  InterlocuteurCompletude,
  NiveauUrgence,
  PALIERS_RELANCE,
  RELANCE_MENSUELLE_JOURS
} from '../types/checklist.types';

// ============================================================================
// DÉFINITION DES DOCUMENTS ATTENDUS
// ============================================================================

/**
 * Stratégie de matching multi-champs :
 * SENTINELLE (l'IA) classe les documents avec un champ `type` générique (ex: "pv", "contrat")
 * et un `titre` descriptif (ex: "PV Assemblée Générale du 18 juillet 2025").
 * Le matching combine donc :
 * - `typesExacts` : valeurs exactes du champ `type` (match direct si spécifique)
 * - `motsClesTitre` : mots-clés cherchés dans `titre` + `typeDocumentLabel` (insensible casse)
 * - `motsExclusion` : mots-clés qui EXCLUENT un match (évite confusions PV AG vs PV réception)
 *
 * Un document matche si :
 *   (type exact match) OU (type générique + au moins un mot-clé titre présent + aucun mot exclusion)
 */

/** Types génériques que SENTINELLE attribue et qui nécessitent un matching par titre */
const TYPES_GENERIQUES = ['pv', 'courrier', 'contrat', 'document', 'facture', 'devis', 'autre'];

interface CatalogueItem {
  id: ChecklistItemId;
  label: string;
  explication: string;
  urgence: NiveauUrgence;
  typesExacts: string[];
  motsClesTitre: string[];
  motsExclusion: string[];
  typesGeneriquesAcceptes: string[];
  conseilObtention: string;
  neuveOnly: boolean;
  bailleurOnly: boolean;
  occupantOnly: boolean;
  /** Section d'affichage dans le widget */
  section: 'copropriete' | 'neuve' | 'bailleur' | 'occupant';
  /** Fraîcheur max en mois — null = pas de péremption */
  fraicheurMaxMois: number | null;
}

const CATALOGUE_DOCUMENTS: CatalogueItem[] = [
  // =====================================================================
  // BASE COMMUNE — Toute copropriété
  // =====================================================================
  {
    id: 'pv_derniere_ag',
    label: 'PV de la dernière assemblée générale',
    explication: 'Le PV d\'AG contient toutes les décisions votées par les copropriétaires. Il est indispensable pour vérifier que le syndic exécute bien ce qui a été décidé.',
    urgence: 'important',
    typesExacts: ['pv_ag', 'pv_assemblee_generale', 'compte_rendu_ag'],
    motsClesTitre: ['assembl', 'ag ', 'ag)', 'assemblée générale', 'assemblee generale'],
    motsExclusion: ['reception', 'réception', 'livraison'],
    typesGeneriquesAcceptes: ['pv'],
    conseilObtention: 'Le syndic est légalement tenu de vous envoyer le PV dans les 2 mois suivant l\'AG (article 17 du décret de 1967). S\'il ne l\'a pas fait, c\'est un manquement. SENTINELLE peut rédiger la demande.',
    neuveOnly: false, bailleurOnly: false, occupantOnly: false,
    section: 'copropriete',
    fraicheurMaxMois: 14 // Un PV d'AG de plus de 14 mois est probablement obsolète
  },
  {
    id: 'reglement_copropriete',
    label: 'Règlement de copropriété',
    explication: 'C\'est la "constitution" de votre copropriété. Il définit les parties communes et privatives, les clés de répartition des charges, et les règles de vie collective.',
    urgence: 'important',
    typesExacts: ['reglement_copropriete', 'reglement', 'rc'],
    motsClesTitre: ['règlement de copropriété', 'reglement de copropriete', 'règlement copropriété', 'reglement copropriete'],
    motsExclusion: [],
    typesGeneriquesAcceptes: ['document', 'contrat'],
    conseilObtention: 'Votre notaire vous l\'a remis lors de l\'achat. Vérifiez vos actes notariés. Sinon, demandez une copie au syndic ou consultez-le sur le site du cadastre (publicité foncière).',
    neuveOnly: false, bailleurOnly: false, occupantOnly: false,
    section: 'copropriete',
    fraicheurMaxMois: null // Pas de péremption
  },
  {
    id: 'contrat_syndic',
    label: 'Contrat de syndic',
    explication: 'Le contrat détaille les missions du syndic, ses honoraires, et les prestations incluses ou facturées en plus. Indispensable pour vérifier qu\'il respecte ses engagements et ne facture pas de prestations non prévues.',
    urgence: 'important',
    typesExacts: ['contrat_syndic', 'mandat_syndic', 'mandat_de_gestion'],
    motsClesTitre: ['contrat de syndic', 'contrat syndic', 'mandat de gestion', 'mandat syndic'],
    motsExclusion: [],
    typesGeneriquesAcceptes: ['contrat', 'document'],
    conseilObtention: 'Le contrat est voté en AG et annexé au PV. Demandez-le au syndic ou vérifiez les annexes de votre dernier PV d\'AG.',
    neuveOnly: false, bailleurOnly: false, occupantOnly: false,
    section: 'copropriete',
    fraicheurMaxMois: null
  },
  {
    id: 'appel_fonds',
    label: 'Appel de fonds trimestriel',
    explication: 'L\'appel de fonds détaille vos charges de copropriété. Il permet de vérifier que le syndic applique les bonnes clés de répartition et ne surfacture pas.',
    urgence: 'important',
    typesExacts: ['appel_fonds', 'appel_de_fonds', 'appel_charges'],
    motsClesTitre: ['appel de fonds', 'appel de charges', 'charges trimestriel', 'provisions trimestriel'],
    motsExclusion: [],
    typesGeneriquesAcceptes: ['facture', 'document', 'courrier'],
    conseilObtention: 'Votre syndic doit vous envoyer un appel de fonds chaque trimestre. Vérifiez votre boîte aux lettres et vos emails. Si vous n\'en avez jamais reçu, c\'est anormal — SENTINELLE peut vous aider à le réclamer.',
    neuveOnly: false, bailleurOnly: false, occupantOnly: false,
    section: 'copropriete',
    fraicheurMaxMois: 5 // Plus de 5 mois = probablement le trimestre précédent manque
  },
  {
    id: 'fiche_synthetique',
    label: 'Fiche synthétique de la copropriété',
    explication: 'Obligatoire depuis la loi ALUR, cette fiche résume les données financières et techniques essentielles de votre copropriété. Le syndic doit la mettre à jour chaque année et la rendre accessible.',
    urgence: 'recommande',
    typesExacts: ['fiche_synthetique'],
    motsClesTitre: ['fiche synthétique', 'fiche synthetique', 'fiche alur'],
    motsExclusion: [],
    typesGeneriquesAcceptes: ['document'],
    conseilObtention: 'Le syndic a l\'obligation légale de la fournir (article 8-2 de la loi du 10 juillet 1965). Demandez-la par email ou courrier. SENTINELLE peut rédiger la demande.',
    neuveOnly: false, bailleurOnly: false, occupantOnly: false,
    section: 'copropriete',
    fraicheurMaxMois: 14
  },
  {
    id: 'attestation_assurance_copro',
    label: 'Attestation d\'assurance de la copropriété',
    explication: 'Elle couvre les parties communes (incendie, dégât des eaux, responsabilité civile). Indispensable pour savoir ce qui est couvert et ce qui ne l\'est pas en cas de sinistre.',
    urgence: 'recommande',
    typesExacts: ['attestation_assurance_copro', 'assurance_copropriete'],
    motsClesTitre: ['assurance copropriété', 'assurance copropriete', 'assurance immeuble', 'multirisque immeuble', 'attestation assurance'],
    motsExclusion: ['pno', 'propriétaire non', 'locataire', 'habitation'],
    typesGeneriquesAcceptes: ['document', 'contrat', 'attestation'],
    conseilObtention: 'Le syndic souscrit cette assurance pour le compte du syndicat. Demandez-lui l\'attestation annuelle. Elle est souvent jointe à la convocation d\'AG.',
    neuveOnly: false, bailleurOnly: false, occupantOnly: false,
    section: 'copropriete',
    fraicheurMaxMois: 14
  },
  {
    id: 'attestation_immatriculation_rnc',
    label: 'Attestation d\'immatriculation au RNC',
    explication: 'Depuis la loi ALUR, toute copropriété doit être immatriculée au Registre National des Copropriétés (RNC). Cette attestation prouve que votre copropriété est en règle. Sans elle, le syndic est en infraction et certaines aides publiques (MaPrimeRénov\' Copro) sont bloquées.',
    urgence: 'recommande',
    typesExacts: ['attestation_immatriculation', 'immatriculation_rnc', 'rnc'],
    motsClesTitre: ['immatriculation', 'rnc', 'registre national', 'registre des copropriétés', 'registre des coproprietes'],
    motsExclusion: [],
    typesGeneriquesAcceptes: ['document', 'attestation'],
    conseilObtention: 'Le syndic est tenu d\'immatriculer la copropriété au RNC (registre-coproprietes.gouv.fr). Demandez-lui l\'attestation. Vous pouvez aussi vérifier l\'immatriculation vous-même sur le site du registre avec le numéro SIRET de votre syndicat.',
    neuveOnly: false, bailleurOnly: false, occupantOnly: false,
    section: 'copropriete',
    fraicheurMaxMois: null // Pas de péremption — immatriculation permanente
  },

  // =====================================================================
  // COPROPRIÉTÉ NEUVE — Documents spécifiques
  // =====================================================================
  {
    id: 'pv_reception_parties_communes',
    label: 'PV de réception des parties communes',
    explication: 'Document critique si votre GPA est en cours. Il prouve l\'état des parties communes à la livraison et liste les réserves. Sans lui, vous perdez votre levier principal contre le promoteur.',
    urgence: 'critique',
    typesExacts: ['pv_reception', 'pv_reception_parties_communes'],
    motsClesTitre: ['reception', 'réception', 'parties communes'],
    motsExclusion: ['assembl', 'ag ', 'livraison'],
    typesGeneriquesAcceptes: ['pv'],
    conseilObtention: 'Demandez-le à votre syndic par courrier recommandé. C\'est lui qui l\'a signé avec le promoteur lors de la réception des parties communes. S\'il ne répond pas sous 30 jours, SENTINELLE peut vous aider à rédiger une mise en demeure.',
    neuveOnly: true, bailleurOnly: false, occupantOnly: false,
    section: 'neuve',
    fraicheurMaxMois: null
  },
  {
    id: 'pv_livraison_lot',
    label: 'PV de livraison de votre lot avec réserves',
    explication: 'Ce document liste les défauts constatés dans votre appartement à la remise des clés. Il déclenche l\'obligation du promoteur de réparer dans le cadre de la garantie de parfait achèvement.',
    urgence: 'critique',
    typesExacts: ['pv_livraison', 'pv_livraison_lot'],
    motsClesTitre: ['livraison', 'remise des cl', 'réserves lot'],
    motsExclusion: ['assembl', 'ag ', 'reception', 'réception'],
    typesGeneriquesAcceptes: ['pv'],
    conseilObtention: 'Vous avez dû le signer le jour de la remise des clés. Vérifiez vos emails et courriers du promoteur. Si vous ne le retrouvez pas, demandez une copie au promoteur par courrier recommandé.',
    neuveOnly: true, bailleurOnly: false, occupantOnly: false,
    section: 'neuve',
    fraicheurMaxMois: null
  },
  {
    id: 'notice_descriptive',
    label: 'Notice descriptive (VEFA)',
    explication: 'La notice descriptive détaille les matériaux, équipements et finitions prévus dans votre lot et les parties communes. C\'est la référence pour identifier les écarts avec ce qui a été livré.',
    urgence: 'important',
    typesExacts: ['notice_descriptive', 'notice_vefa'],
    motsClesTitre: ['notice descriptive', 'notice technique', 'descriptif technique', 'vefa'],
    motsExclusion: [],
    typesGeneriquesAcceptes: ['document'],
    conseilObtention: 'Elle fait partie de votre contrat de réservation (VEFA). Cherchez dans vos documents d\'achat ou demandez une copie au promoteur.',
    neuveOnly: true, bailleurOnly: false, occupantOnly: false,
    section: 'neuve',
    fraicheurMaxMois: null
  },
  {
    id: 'attestation_dommages_ouvrage',
    label: 'Attestation d\'assurance dommages-ouvrage',
    explication: 'L\'assurance DO couvre les désordres relevant de la garantie décennale sans attendre une décision de justice. Elle permet un préfinancement rapide des réparations lourdes (fissures, infiltrations, structure).',
    urgence: 'important',
    typesExacts: ['dommages_ouvrage', 'attestation_do'],
    motsClesTitre: ['dommages-ouvrage', 'dommages ouvrage', 'assurance do', 'décennale'],
    motsExclusion: ['pno', 'habitation', 'locataire'],
    typesGeneriquesAcceptes: ['document', 'contrat', 'attestation'],
    conseilObtention: 'Le promoteur est tenu de souscrire une DO avant l\'ouverture du chantier. Demandez l\'attestation au promoteur ou au syndic. Si elle n\'existe pas, c\'est un problème grave que SENTINELLE peut vous aider à signaler.',
    neuveOnly: true, bailleurOnly: false, occupantOnly: false,
    section: 'neuve',
    fraicheurMaxMois: null
  },
  {
    id: 'carnet_entretien',
    label: 'Carnet d\'entretien de l\'immeuble',
    explication: 'Obligatoire, il recense les travaux réalisés et à prévoir sur l\'immeuble. Il permet de planifier l\'entretien et de comprendre l\'historique technique de la copropriété.',
    urgence: 'recommande',
    typesExacts: ['carnet_entretien'],
    motsClesTitre: ['carnet d\'entretien', 'carnet entretien', 'carnet immeuble'],
    motsExclusion: [],
    typesGeneriquesAcceptes: ['document'],
    conseilObtention: 'Le syndic est tenu de le créer et de le mettre à jour (article 18 de la loi du 10 juillet 1965). Demandez-le lui. Pour une copropriété neuve, il est souvent quasi vide mais doit exister.',
    neuveOnly: true, bailleurOnly: false, occupantOnly: false,
    section: 'neuve',
    fraicheurMaxMois: null
  },

  // =====================================================================
  // OCCUPANT — Documents spécifiques (statutOccupation = occupant ou mixte)
  // =====================================================================
  {
    id: 'assurance_habitation',
    label: 'Attestation d\'assurance habitation',
    explication: 'Obligatoire pour tout occupant, l\'assurance multirisque habitation couvre les dommages dans votre logement (incendie, dégât des eaux, vol). Le syndic peut vous la demander chaque année.',
    urgence: 'important',
    typesExacts: ['assurance_habitation', 'mrh', 'multirisque_habitation'],
    motsClesTitre: ['assurance habitation', 'multirisque habitation', 'mrh', 'attestation habitation'],
    motsExclusion: ['pno', 'propriétaire non', 'copropriété', 'immeuble'],
    typesGeneriquesAcceptes: ['document', 'contrat', 'attestation'],
    conseilObtention: 'Contactez votre assureur pour obtenir l\'attestation annuelle. Si vous n\'avez pas encore d\'assurance habitation, c\'est une obligation légale à régulariser en priorité.',
    neuveOnly: false, bailleurOnly: false, occupantOnly: true,
    section: 'occupant',
    fraicheurMaxMois: 14 // Renouvellement annuel
  },

  // =====================================================================
  // BAILLEUR — Documents spécifiques (statutOccupation = bailleur ou mixte)
  // =====================================================================
  {
    id: 'assurance_pno',
    label: 'Attestation d\'assurance PNO',
    explication: 'L\'assurance propriétaire non-occupant (PNO) est fortement recommandée (et souvent exigée par le syndic). Elle couvre votre responsabilité en cas de sinistre dans votre lot loué, même si votre locataire a sa propre assurance.',
    urgence: 'important',
    typesExacts: ['assurance_pno', 'attestation_pno'],
    motsClesTitre: ['pno', 'propriétaire non-occupant', 'proprietaire non occupant', 'assurance bailleur', 'logement locatif', 'habitation locatif', 'formule locatif', 'non-occupant', 'non occupant'],
    motsExclusion: ['habitation occupant', 'multirisque habitation'],
    typesGeneriquesAcceptes: ['document', 'contrat', 'attestation'],
    conseilObtention: 'Contactez votre assureur ou comparez les offres en ligne. Le coût est généralement de 50 à 150 € par an. C\'est un investissement essentiel pour protéger votre patrimoine.',
    neuveOnly: false, bailleurOnly: true, occupantOnly: false,
    section: 'bailleur',
    fraicheurMaxMois: 14
  },
  {
    id: 'bail_location',
    label: 'Bail de location',
    explication: 'Le bail est le contrat qui régit la relation avec votre locataire. Il fixe le loyer, les charges récupérables, la durée, et les obligations de chaque partie. SENTINELLE peut l\'analyser pour vérifier sa conformité.',
    urgence: 'important',
    typesExacts: ['bail', 'bail_location', 'contrat_location'],
    motsClesTitre: ['bail', 'contrat de location', 'contrat location', 'contrat de bail'],
    motsExclusion: [],
    typesGeneriquesAcceptes: ['contrat', 'document'],
    conseilObtention: 'Si vous avez confié la gestion à une agence, demandez-leur une copie. Sinon, retrouvez le bail signé lors de l\'entrée du locataire.',
    neuveOnly: false, bailleurOnly: true, occupantOnly: false,
    section: 'bailleur',
    fraicheurMaxMois: null
  },
  {
    id: 'diagnostics_locatifs',
    label: 'Dossier de diagnostics techniques (DDT)',
    explication: 'Le DDT est obligatoire pour toute mise en location : DPE, électricité, gaz, amiante, plomb, ERP. Sans diagnostics valides, votre bail peut être contesté et votre responsabilité engagée.',
    urgence: 'important',
    typesExacts: ['diagnostics', 'ddt', 'diagnostics_locatifs'],
    motsClesTitre: ['diagnostic', 'dpe', 'diagnostics technique', 'dossier diagnostic', 'performance énergétique', 'performance energetique'],
    motsExclusion: [],
    typesGeneriquesAcceptes: ['document'],
    conseilObtention: 'Faites appel à un diagnostiqueur certifié. Les diagnostics ont des durées de validité variables (6 mois à 10 ans selon le type). Vérifiez que les vôtres sont à jour.',
    neuveOnly: false, bailleurOnly: true, occupantOnly: false,
    section: 'bailleur',
    fraicheurMaxMois: null // Validité variable selon le type de diagnostic
  },
  {
    id: 'etat_des_lieux',
    label: 'État des lieux d\'entrée',
    explication: 'L\'état des lieux d\'entrée est la référence pour comparer avec l\'état de sortie. Sans lui, vous ne pourrez pas retenir de montant sur le dépôt de garantie en cas de dégradations.',
    urgence: 'recommande',
    typesExacts: ['etat_des_lieux', 'edl'],
    motsClesTitre: ['état des lieux', 'etat des lieux', 'edl entrée', 'edl entree'],
    motsExclusion: ['sortie'],
    typesGeneriquesAcceptes: ['document'],
    conseilObtention: 'Il a été rédigé lors de la remise des clés au locataire. Si vous êtes passé par une agence, demandez-leur une copie.',
    neuveOnly: false, bailleurOnly: true, occupantOnly: false,
    section: 'bailleur',
    fraicheurMaxMois: null
  }
];

// ============================================================================
// GÉNÉRATION DE LA CHECKLIST PERSONNALISÉE
// ============================================================================

/**
 * Génère la checklist personnalisée selon le profil complet de la copropriété.
 * - Base commune : 7 documents (toute copro, incluant attestation RNC)
 * - Neuve : +5 documents spécifiques (PV réception, livraison, DO, notice, carnet)
 * - Bailleur/mixte : +4 documents spécifiques (PNO, bail, diagnostics, EDL)
 * L'urgence est rehaussée dynamiquement si la GPA expire bientôt.
 */
export function genererChecklistVierge(
  coproprieteNeuve: boolean,
  garanties?: { gpa?: string },
  statutOccupation?: string
): ChecklistItem[] {
  const estBailleur = statutOccupation === 'bailleur' || statutOccupation === 'mixte';
  const estOccupant = statutOccupation === 'occupant' || statutOccupation === 'mixte';

  // Filtrer selon profil (neuve + bailleur + occupant)
  const items: ChecklistItem[] = CATALOGUE_DOCUMENTS
    .filter(item => {
      if (item.neuveOnly && !coproprieteNeuve) return false;
      if (item.bailleurOnly && !estBailleur) return false;
      if (item.occupantOnly && !estOccupant) return false;
      return true;
    })
    .map(item => ({
      id: item.id,
      label: item.label,
      explication: item.explication,
      urgence: item.urgence,
      typesDocumentMatch: item.typesExacts,
      conseilObtention: item.conseilObtention,
      neuveOnly: item.neuveOnly,
      bailleurOnly: item.bailleurOnly,
      occupantOnly: item.occupantOnly,
      section: item.section,
      fraicheurMaxMois: item.fraicheurMaxMois,
      statut: 'manquant' as const
    }));

  // Rehausser l'urgence si GPA expire dans moins de 6 mois
  if (coproprieteNeuve && garanties?.gpa) {
    const dateGpa = new Date(garanties.gpa);
    const maintenant = new Date();
    const joursRestants = Math.floor((dateGpa.getTime() - maintenant.getTime()) / (1000 * 60 * 60 * 24));

    if (joursRestants > 0 && joursRestants <= 180) {
      // PV réception et PV livraison deviennent critiques + message urgent
      const pvReception = items.find(i => i.id === 'pv_reception_parties_communes');
      if (pvReception) {
        pvReception.urgence = 'critique';
        pvReception.explication = `URGENT — Votre garantie de parfait achèvement expire dans ${joursRestants} jours (${dateGpa.toLocaleDateString('fr-FR')}). ${pvReception.explication}`;
      }
      const pvLivraison = items.find(i => i.id === 'pv_livraison_lot');
      if (pvLivraison) {
        pvLivraison.urgence = 'critique';
        pvLivraison.explication = `URGENT — GPA expire dans ${joursRestants} jours. ${pvLivraison.explication}`;
      }
    }
  }

  return items;
}

// ============================================================================
// MATCHING AVEC LES DOCUMENTS DÉPOSÉS
// ============================================================================

/**
 * Compare les documents déposés en Firestore avec la checklist attendue.
 * Matching multi-champs : type exact OU (type générique + mots-clés dans titre - exclusions).
 */
export async function calculerChecklistState(coproprieteId: string): Promise<ChecklistState> {
  // 1. Récupérer le profil onboarding
  const coproDoc = await getDoc(doc(db, 'coproprietes', coproprieteId));
  if (!coproDoc.exists()) {
    throw new Error('Copropriété introuvable');
  }

  const coproData = coproDoc.data();
  const coproprieteNeuve = coproData.coproprieteNeuve === true;
  const garanties = coproData.garanties || coproData.onboardingData?.garanties;
  const statutOccupation = coproData.statutOccupation || coproData.onboardingData?.statutOccupation || 'occupant';

  // 2. Générer la checklist vierge personnalisée (neuve + bailleur)
  const items = genererChecklistVierge(coproprieteNeuve, garanties, statutOccupation);

  // 3. Récupérer tous les documents déposés pour cette copropriété
  const docsQuery = query(
    collection(db, 'documents'),
    where('coproprieteId', '==', coproprieteId)
  );
  const docsSnap = await getDocs(docsQuery);

  // 4. Construire deux index :
  //    — indexParChecklistId : documents ayant un checklistItemId posé par SENTINELLE (fiable)
  //    — documentsDeposes    : tous les documents pour le fallback mots-clés
  const indexParChecklistId = new Map<string, { id: string; titre: string; date: Date }[]>();
  const documentsDeposes: {
    type: string;
    titre: string;
    typeDocumentLabel: string;
    id: string;
    date: Date;
  }[] = [];

  docsSnap.forEach((docSnap) => {
    const data = docSnap.data();
    const date = data.dateDocument?.toDate?.() || data.createdAt?.toDate?.() || new Date();
    const id = docSnap.id;
    const titre = data.titre || data.fichierNom || 'Document';

    // Session 244 — Passe 1 : indexation par checklistItemId
    const checklistItemId = data.checklistItemId as string | undefined;
    if (checklistItemId) {
      if (!indexParChecklistId.has(checklistItemId)) {
        indexParChecklistId.set(checklistItemId, []);
      }
      indexParChecklistId.get(checklistItemId)!.push({ id, titre, date });
    }

    // Passe 2 fallback : index enrichi pour matching mots-clés
    documentsDeposes.push({
      type: (data.type || data.typeDocument || '').toLowerCase().trim(),
      titre: titre.toLowerCase(),
      typeDocumentLabel: (data.typeDocumentLabel || '').toLowerCase(),
      id,
      date
    });
  });

  // 5. Matcher chaque item
  const catalogueIndex = new Map<ChecklistItemId, CatalogueItem>();
  for (const cat of CATALOGUE_DOCUMENTS) {
    catalogueIndex.set(cat.id, cat);
  }

  for (const item of items) {
    const catalogue = catalogueIndex.get(item.id);
    if (!catalogue) continue;

    // ── Passe 1 : matching direct sur checklistItemId (source fiable) ──────
    const matchesParId = indexParChecklistId.get(item.id) || [];

    // ── Passe 2 fallback : matching mots-clés (documents sans checklistItemId) ──
    // On exclut les docs déjà trouvés en passe 1 pour éviter les doublons
    const idsDejaMatches = new Set(matchesParId.map(m => m.id));
    const matchesFallback = documentsDeposes.filter(
      d => !idsDejaMatches.has(d.id) && matchDocument(d, catalogue)
    );

    // Fusionner les deux sources, passe 1 en priorité
    const tousMatches = [
      ...matchesParId,
      ...matchesFallback.map(d => ({ id: d.id, titre: docsSnap.docs.find(ds => ds.id === d.id)?.data()?.titre || d.titre, date: d.date }))
    ];

    if (tousMatches.length > 0) {
      // Prendre le plus récent toutes sources confondues
      const plusRecent = tousMatches.sort((a, b) => b.date.getTime() - a.date.getTime())[0];

      // Vérifier la fraîcheur si applicable
      if (catalogue.fraicheurMaxMois !== null) {
        const maintenant = new Date();
        const ageMois = (maintenant.getTime() - plusRecent.date.getTime()) / (1000 * 60 * 60 * 24 * 30.44);
        if (ageMois > catalogue.fraicheurMaxMois) {
          item.statut = 'perime';
          item.documentId = plusRecent.id;
          item.documentTitre = plusRecent.titre;
          item.documentDate = plusRecent.date;
          continue;
        }
      }

      item.statut = 'depose';
      item.documentId = plusRecent.id;
      item.documentTitre = plusRecent.titre;
      item.documentDate = plusRecent.date;
    } else {
      item.statut = item.urgence === 'critique' ? 'urgent' : 'manquant';
    }
  }

  // 6. Trier : par section d'abord (ordre fixe), puis par statut à l'intérieur de chaque section
  const ordreSection: Record<string, number> = { 'neuve': 0, 'copropriete': 1, 'occupant': 2, 'bailleur': 3 };
  const ordreStatut: Record<string, number> = { 'urgent': 0, 'perime': 1, 'manquant': 2, 'depose': 3 };
  const ordreUrgence: Record<string, number> = { 'critique': 0, 'important': 1, 'recommande': 2 };
  items.sort((a, b) => {
    // D'abord par section
    const diffSection = (ordreSection[a.section] ?? 9) - (ordreSection[b.section] ?? 9);
    if (diffSection !== 0) return diffSection;
    // Puis par statut dans la section
    const diffStatut = (ordreStatut[a.statut] ?? 9) - (ordreStatut[b.statut] ?? 9);
    if (diffStatut !== 0) return diffStatut;
    // Enfin par urgence
    return (ordreUrgence[a.urgence] ?? 9) - (ordreUrgence[b.urgence] ?? 9);
  });

  // 7. Calculer les totaux (périmés comptent comme manquants)
  const totalAttendus = items.length;
  const totalDeposes = items.filter(i => i.statut === 'depose').length;
  const totalManquants = totalAttendus - totalDeposes; // urgent + manquant + périmé
  const totalPerimes = items.filter(i => i.statut === 'perime').length;
  const tauxCompletude = totalAttendus > 0 ? Math.round((totalDeposes / totalAttendus) * 100) : 100;
  const alerteCritique = items.some(i => i.statut === 'urgent');

  return {
    items,
    totalAttendus,
    totalDeposes,
    totalManquants,
    tauxCompletude,
    alerteCritique,
    dateCalcul: new Date()
  };
}

/**
 * Vérifie si un document Firestore correspond à un item du catalogue.
 *
 * Règle :
 * 1. Si le type du document est dans `typesExacts` → match direct
 * 2. Si le type est générique (pv, contrat, etc.) ET dans `typesGeneriquesAcceptes` :
 *    → chercher au moins un mot-clé de `motsClesTitre` dans titre OU typeDocumentLabel
 *    → vérifier qu'aucun mot de `motsExclusion` n'est présent
 * 3. Dans tous les cas, si le titre contient un mot-clé ET pas d'exclusion → match par titre seul
 */
function matchDocument(
  doc: { type: string; titre: string; typeDocumentLabel: string },
  catalogue: CatalogueItem
): boolean {
  const { type, titre, typeDocumentLabel } = doc;
  const texteComplet = `${titre} ${typeDocumentLabel}`;

  // 1. Match exact sur le type
  if (catalogue.typesExacts.some(t => type === t.toLowerCase())) {
    return true;
  }

  // 2. Type générique + mots-clés titre
  const estTypeGenerique = TYPES_GENERIQUES.includes(type);
  const typeGeneriqueAccepte = catalogue.typesGeneriquesAcceptes.some(t => type === t.toLowerCase());

  if (estTypeGenerique && typeGeneriqueAccepte) {
    const aMotCle = catalogue.motsClesTitre.some(mc => texteComplet.includes(mc.toLowerCase()));
    const aExclusion = catalogue.motsExclusion.length > 0 &&
      catalogue.motsExclusion.some(me => texteComplet.includes(me.toLowerCase()));

    if (aMotCle && !aExclusion) {
      return true;
    }
  }

  // 3. Fallback : matching par titre seul (même si type inattendu)
  // Utile si SENTINELLE attribue un type qu'on n'avait pas prévu
  if (catalogue.motsClesTitre.some(mc => texteComplet.includes(mc.toLowerCase()))) {
    const aExclusion = catalogue.motsExclusion.length > 0 &&
      catalogue.motsExclusion.some(me => texteComplet.includes(me.toLowerCase()));
    if (!aExclusion) {
      return true;
    }
  }

  return false;
}

// ============================================================================
// RELANCES PROGRESSIVES
// ============================================================================

/**
 * Récupère l'état de relance depuis Firestore.
 */
export async function getRelanceState(coproprieteId: string): Promise<RelanceState> {
  const relanceDoc = await getDoc(doc(db, 'coproprietes', coproprieteId));
  if (!relanceDoc.exists()) {
    return {
      derniereRelance: null,
      nombreRelances: 0,
      itemsSignales: [],
      itemsNonDisponibles: []
    };
  }

  const data = relanceDoc.data();
  const relanceData = data.checklistRelance;

  if (!relanceData) {
    return {
      derniereRelance: null,
      nombreRelances: 0,
      itemsSignales: [],
      itemsNonDisponibles: []
    };
  }

  return {
    derniereRelance: relanceData.derniereRelance?.toDate?.() || null,
    nombreRelances: relanceData.nombreRelances || 0,
    itemsSignales: relanceData.itemsSignales || [],
    itemsNonDisponibles: relanceData.itemsNonDisponibles || []
  };
}

/**
 * Détermine si SENTINELLE doit afficher un bandeau de relance.
 * Logique : J+3, J+10, J+21 puis mensuelle (J+51, J+81, ...).
 * Ne relance JAMAIS si tout est complet.
 */
export function doitRelancer(
  relanceState: RelanceState,
  checklistState: ChecklistState,
  dateOnboardingComplete: Date | null
): boolean {
  // Si tout est déposé → pas de relance
  if (checklistState.totalManquants === 0) return false;

  // Si pas de date d'onboarding → on considère qu'on peut relancer
  if (!dateOnboardingComplete) return true;

  const maintenant = new Date();
  const joursDepuisOnboarding = Math.floor(
    (maintenant.getTime() - dateOnboardingComplete.getTime()) / (1000 * 60 * 60 * 24)
  );

  // Trouver le prochain palier applicable
  const prochainPalier = trouverProchainPalier(joursDepuisOnboarding, relanceState.nombreRelances);

  if (prochainPalier === null) return false;

  // Vérifier qu'on n'a pas déjà relancé aujourd'hui
  if (relanceState.derniereRelance) {
    const joursDepuisDerniereRelance = Math.floor(
      (maintenant.getTime() - relanceState.derniereRelance.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (joursDepuisDerniereRelance < 1) return false;
  }

  return joursDepuisOnboarding >= prochainPalier;
}

/**
 * Trouve le prochain palier de relance (en jours) applicable.
 */
function trouverProchainPalier(joursDepuisOnboarding: number, nombreRelancesFaites: number): number | null {
  // D'abord les paliers fixes (J+3, J+10, J+21)
  for (const palier of PALIERS_RELANCE) {
    if (joursDepuisOnboarding >= palier && nombreRelancesFaites < PALIERS_RELANCE.indexOf(palier) + 1) {
      return palier;
    }
  }

  // Ensuite mensuel (J+51, J+81, J+111, ...)
  const dernierPalierFixe = PALIERS_RELANCE[PALIERS_RELANCE.length - 1]; // 21
  const relancesFixesFaites = PALIERS_RELANCE.length; // 3

  if (nombreRelancesFaites >= relancesFixesFaites) {
    const relancesMensuellesFaites = nombreRelancesFaites - relancesFixesFaites;
    const prochainPalierMensuel = dernierPalierFixe + RELANCE_MENSUELLE_JOURS * (relancesMensuellesFaites + 1);
    if (joursDepuisOnboarding >= prochainPalierMensuel) {
      return prochainPalierMensuel;
    }
  }

  return null;
}

/**
 * Enregistre qu'une relance a été affichée à Nicole.
 */
export async function enregistrerRelance(
  coproprieteId: string,
  itemsManquants: ChecklistItemId[]
): Promise<void> {
  const relanceState = await getRelanceState(coproprieteId);

  await updateDoc(doc(db, 'coproprietes', coproprieteId), {
    'checklistRelance.derniereRelance': serverTimestamp(),
    'checklistRelance.nombreRelances': relanceState.nombreRelances + 1,
    'checklistRelance.itemsSignales': itemsManquants,
    updatedAt: serverTimestamp()
  });
}

/**
 * Marque un item comme "non disponible" (Nicole a cliqué "Je ne l'ai pas").
 * SENTINELLE ne l'exclut pas mais réduit sa priorité dans les relances.
 */
export async function marquerItemNonDisponible(
  coproprieteId: string,
  itemId: ChecklistItemId
): Promise<void> {
  const relanceState = await getRelanceState(coproprieteId);
  const itemsNonDisponibles = Array.from(new Set([...relanceState.itemsNonDisponibles, itemId]));

  await updateDoc(doc(db, 'coproprietes', coproprieteId), {
    'checklistRelance.itemsNonDisponibles': itemsNonDisponibles,
    updatedAt: serverTimestamp()
  });
}

// ============================================================================
// COMPLÉTUDE DES INTERLOCUTEURS
// ============================================================================

/**
 * Vérifie si les interlocuteurs clés (syndic, promoteur) ont des coordonnées complètes.
 * Retourne la liste des interlocuteurs avec leurs champs manquants.
 */
export async function verifierCompletudeInterlocuteurs(
  coproprieteId: string
): Promise<InterlocuteurCompletude[]> {
  // Un compte VIGIE solo n'a pas d'interlocuteurs CS a verifier
  if (coproprieteId.startsWith('vigie-')) return [];

  const coproDoc = await getDoc(doc(db, 'coproprietes', coproprieteId));
  if (!coproDoc.exists()) return [];

  const coproData = coproDoc.data();
  const syndicId = coproData.syndicInterlocuteurId;
  const promoteurId = coproData.promoteurInterlocuteurId;

  const resultats: InterlocuteurCompletude[] = [];

  // Vérifier le syndic
  if (syndicId) {
    const completude = await verifierUnInterlocuteur(syndicId, 'syndic', coproprieteId);
    if (completude) resultats.push(completude);
  }

  // Vérifier le promoteur (uniquement si copro neuve)
  if (promoteurId && coproData.coproprieteNeuve) {
    const completude = await verifierUnInterlocuteur(promoteurId, 'promoteur', coproprieteId);
    if (completude) resultats.push(completude);
  }

  return resultats;
}

/**
 * Vérifie un interlocuteur spécifique et retourne ses champs manquants.
 */
async function verifierUnInterlocuteur(
  interlocuteurId: string,
  type: 'syndic' | 'promoteur',
  coproprieteId: string
): Promise<InterlocuteurCompletude | null> {
  try {
    const interlocDoc = await getDoc(doc(db, 'interlocuteurs', interlocuteurId));
    if (!interlocDoc.exists()) return null;

    const data = interlocDoc.data();
    const champsManquants: ('email' | 'adresse' | 'telephone' | 'contactPrincipal')[] = [];

    // Vérifier les contacts
    const contacts = data.contacts || [];
    const premierContact = contacts[0] || {};

    if (!premierContact.email && !data.email) {
      champsManquants.push('email');
    }
    if (!data.adresse && !data.adressePostale) {
      champsManquants.push('adresse');
    }
    if (!premierContact.telephone && !data.telephone) {
      champsManquants.push('telephone');
    }
    if (contacts.length === 0 || !premierContact.nom) {
      champsManquants.push('contactPrincipal');
    }

    return {
      interlocuteurId,
      type,
      raisonSociale: data.raisonSociale || data.nom || 'Non renseigné',
      contactComplet: champsManquants.length === 0,
      champsManquants
    };
  } catch (error) {
    console.error(`[Checklist] Erreur vérification interlocuteur ${interlocuteurId}:`, error);
    return null;
  }
}

// ============================================================================
// FONCTION COMBINÉE — ÉTAT COMPLET DU COCKPIT
// ============================================================================

/** Résultat complet pour le cockpit SENTINELLE */
export interface ChecklistCockpitData {
  checklist: ChecklistState;
  relance: RelanceState;
  doitAfficherRelance: boolean;
  interlocuteurs: InterlocuteurCompletude[];
  interlocuteursIncomplets: InterlocuteurCompletude[];
}

/**
 * Charge toutes les données nécessaires au bandeau et widget checklist du cockpit.
 * Un seul appel pour tout récupérer.
 */
export async function chargerChecklistCockpit(coproprieteId: string): Promise<ChecklistCockpitData> {
  // Paralléliser les 3 requêtes
  const [checklist, relance, interlocuteurs] = await Promise.all([
    calculerChecklistState(coproprieteId),
    getRelanceState(coproprieteId),
    verifierCompletudeInterlocuteurs(coproprieteId)
  ]);

  // Récupérer la date de complétion de l'onboarding pour le calcul de relance
  const coproDoc = await getDoc(doc(db, 'coproprietes', coproprieteId));
  let dateOnboardingComplete: Date | null = null;
  if (coproDoc.exists()) {
    const data = coproDoc.data();
    const onboardingTs = data.onboardingData?.dateReponses || data.onboardingCompletedAt;
    if (onboardingTs?.toDate) {
      dateOnboardingComplete = onboardingTs.toDate();
    }
  }

  const doitAfficherRelance = doitRelancer(relance, checklist, dateOnboardingComplete);
  const interlocuteursIncomplets = interlocuteurs.filter(i => !i.contactComplet);

  return {
    checklist,
    relance,
    doitAfficherRelance,
    interlocuteurs,
    interlocuteursIncomplets
  };
}

// ============================================================================
// SESSION 244 : MIGRATION RÉTROACTIVE — POSE DES checklistItemId
// Passe sur tous les documents existants d'une copropriété,
// applique le matching mots-clés actuel, et pose checklistItemId
// sur ceux qui sont reconnus (confiance 'auto').
// À appeler une fois par copropriété depuis la console admin ou un script.
// ============================================================================

/**
 * Migre les documents existants d'une copropriété en posant checklistItemId
 * sur ceux que le matching mots-clés identifie.
 *
 * Retourne un rapport : nb docs traités, nb matchés, nb ignorés (déjà tagués).
 */
export async function migrerChecklistItemIds(coproprieteId: string): Promise<{
  traites: number;
  matchés: number;
  ignorés: number;
  détails: { documentId: string; checklistItemId: string; titre: string }[];
}> {
  const docsQuery = query(
    collection(db, 'documents'),
    where('coproprieteId', '==', coproprieteId)
  );
  const docsSnap = await getDocs(docsQuery);

  let traites = 0;
  let matchés = 0;
  let ignorés = 0;
  const détails: { documentId: string; checklistItemId: string; titre: string }[] = [];

  for (const docSnap of docsSnap.docs) {
    const data = docSnap.data();
    traites++;

    // Ignorer les documents déjà taggués
    if (data.checklistItemId) {
      ignorés++;
      continue;
    }

    // Construire l'objet enrichi pour matchDocument
    const docEnrichi = {
      type: (data.type || data.typeDocument || '').toLowerCase().trim(),
      titre: (data.titre || data.fichierNom || '').toLowerCase(),
      typeDocumentLabel: (data.typeDocumentLabel || '').toLowerCase(),
    };

    // Tester chaque item du catalogue
    let itemTrouve: string | null = null;
    for (const cat of CATALOGUE_DOCUMENTS) {
      if (matchDocument(docEnrichi, cat)) {
        itemTrouve = cat.id;
        break; // Premier match — un document = un item
      }
    }

    if (itemTrouve) {
      try {
        await updateDoc(doc(db, 'documents', docSnap.id), {
          checklistItemId: itemTrouve,
          checklistMatchConfiance: 'auto', // Distingue du matching SENTINELLE ('sentinelle')
          updatedAt: serverTimestamp()
        });
        matchés++;
        détails.push({
          documentId: docSnap.id,
          checklistItemId: itemTrouve,
          titre: data.titre || data.fichierNom || 'Sans titre'
        });
      } catch (err) {
        console.error(`[migrerChecklistItemIds] Erreur update doc ${docSnap.id}:`, err);
      }
    }
  }

  console.log(`[migrerChecklistItemIds] ${coproprieteId} — traités: ${traites}, matchés: ${matchés}, ignorés: ${ignorés}`);
  return { traites, matchés, ignorés, détails };
}

// ============================================================================
// MESSAGE DE RELANCE CONTEXTUEL
// ============================================================================

/**
 * Génère le message de relance adapté au contexte.
 * Priorise les documents critiques et tient compte de l'historique.
 */
export function genererMessageRelance(
  checklist: ChecklistState,
  relance: RelanceState
): string {
  const manquants = checklist.items.filter(i => i.statut !== 'depose');
  const critiques = manquants.filter(i => i.urgence === 'critique');
  const nonSignales = manquants.filter(i => !relance.itemsNonDisponibles.includes(i.id));

  // Priorité 1 : documents critiques non marqués "non disponible"
  const critiquesActifs = critiques.filter(i => !relance.itemsNonDisponibles.includes(i.id));

  if (critiquesActifs.length > 0) {
    const premier = critiquesActifs[0];
    return `${premier.label} — ${premier.explication}`;
  }

  // Priorité 2 : autres documents manquants
  if (nonSignales.length > 0) {
    const total = nonSignales.length;
    return `Il vous manque encore ${total} document${total > 1 ? 's' : ''} pour compléter votre dossier. SENTINELLE vous accompagne pour les obtenir.`;
  }

  // Tout est soit déposé soit marqué "non disponible"
  return 'Votre dossier progresse. N\'hésitez pas à déposer vos documents dès que vous les recevez.';
}
